import { useEffect, useRef, useState } from 'react';
import { LiikkuvaWifiSalesProductOnlineModel } from '../../common/constants/subscriptionConstants.js';
import { ModelType, VirtualModelType } from '../../common/enums.js';
import { fetchAddOnRules } from '../../common/fetch.js';
import { exploreAndOrderMsg, t } from '../../common/i18n/index.js';
import { fetchVoucherCampaigns } from '../../common/utils/voucherCampaignUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import {
  expandRelatedModelCodes,
  filterByOnlineModelCodes,
  mergeMobileBroadbandModels,
} from '../../public/site/onlineModel/onlineModelService.js';
import { HorizontalM2MSubscriptions } from '../HorizontalSubscriptionCard/HorizontalM2MSubscriptions.js';
import { HorizontalMBBLiikkuvaWifiSubscriptions } from '../HorizontalSubscriptionCard/HorizontalMBBLiikkuvaWifiSubscriptions.js';
import { HorizontalMBBYritysdataSubscriptions } from '../HorizontalSubscriptionCard/HorizontalMBBYritysdataSubscriptions';
import { HorizontalMobileBroadbandPublicSubscriptions } from '../HorizontalSubscriptionCard/HorizontalMobileBroadbandPublicSubscriptions.js';
import { HorizontalVoiceSubscriptions } from '../HorizontalSubscriptionCard/HorizontalVoiceSubscriptions.js';
import { SubscriptionModal } from '../HorizontalSubscriptionCard/SubscriptionModal.js';
import { addOfferToBasket } from '../HorizontalSubscriptionCard/SubscriptionUtils.js';
import {
  getPuhePlusModalDescriptionItems,
  getPuheProModalDescriptionItems,
} from '../HorizontalSubscriptionCard/VoiceSubscriptionCards.js';
import {
  type SubCardWithAnalyticsV4Props,
  getAnalyticsListName,
} from '../HorizontalSubscriptionCard/subscriptionCardsAnalyticsUtils.js';
import { getModalDescriptionItems } from '../HorizontalSubscriptionCard/subscriptionModalContentUtils.js';
import { Loading } from '../Loading/Loading.js';
import { addSubscriptionToShoppingBasket } from '../ShoppingBasket/shoppingBasketFunctions.js';
import { getDefaultSelectedLanguage, isMobileVoiceAddOn } from '../SubscriptionDetails/addOnDependencyUtils.js';
import { getOnlineModel } from './OrderSubscriptionSelection.js';
import {
  type OfferProductMapType,
  getCardContentWithServiceFee,
  getSubscriptionCardStaticContent,
  prepareOfferAndServiceFeeMap,
} from './OrderSubscriptionSelectionUtils.js';
import { type OfferPerGroup, getSubscriptionCardContent } from './subscriptionProductCardUtils.js';
import type { OnlineModelType } from '../../common/enums.js';
import type { CLPopoverImperativeRefHandle } from '../../common/types/componentLibrary';
import type { CampaignContext } from '../../generated/api/campaignContext.js';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion.js';
import type { ChildOrderItem } from '../../generated/api/childOrderItem.js';
import type { OnlineModel } from '../../generated/api/onlineModel.js';
import type { AssociationRecord } from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { ModalDescriptionItem } from '../HorizontalSubscriptionCard/SubscriptionModal.js';
import type {
  OrderProps,
  PublicPageOrderProps,
  SubscriptionCardWithModel,
} from '../HorizontalSubscriptionCard/types.js';
import type { MobileBroadbandSubCardProps, SubscriptionCardProps } from '../SubscriptionCard/SubscriptionCard.js';

/**
 * Wrapper that hydrates subscription cards with model data fetched via OnlineModelContextProvider.
 *
 * 1. Request models via context (cached/batched across components)
 * 2. Normalize with getOnlineModel to create synthetic offers
 * 3. Build card content with pricing and service fees
 * 4. Add publicPage metadata (offerCode, modelCode, addonCode) for basket handling
 */

export interface SubscriptionCardsByModelTypeWrapperProps {
  onlineModelType: OnlineModelType;
  headerFilter?: string[];
  wide?: boolean;
  compact?: boolean;
  eventOrigin: string;
}

export const SupportedSubscriptionCardModels: OnlineModelType[] = [
  ModelType.MobileM2M,
  ModelType.MobileBroadband,
  VirtualModelType.LiikkuvaWifi,
  ModelType.YrityspuheUusi,
];

export const SubscriptionCardsByModelTypeWrapper = ({
  onlineModelType,
  headerFilter,
  wide,
  compact,
  eventOrigin,
}: SubscriptionCardsByModelTypeWrapperProps) => {
  const { authenticatedUser } = useAuth();
  const { priceAlteredModels, fetchModelsByCodes, activeVoucherCode } = useOnlineModelsContext();
  const [normalizedModel, setNormalizedModel] = useState<OnlineModel | undefined>();
  const [offerAndFeeMap, setOfferAndFeeMap] = useState<OfferProductMapType[]>([]);
  const [cloudSenseAssociations, setCloudSenseAssociations] = useState<AssociationRecord[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<SubscriptionCardWithModel | undefined>();
  const [selectedCompactCardName, setSelectedCompactCardName] = useState<string>();
  const [selectedModalDescriptionItems, setSelectedModalDescriptionItems] = useState<ModalDescriptionItem[]>([]);
  const modalActivePopoverRef = useRef<CLPopoverImperativeRefHandle>(null);

  const allStaticContent = getSubscriptionCardStaticContent(onlineModelType, false);
  const normalizedHeaderFilters = headerFilter?.map(filter => filter.trim().replace(/^'+/, '').toLowerCase());

  const mapToDefaultSelection = (cards: SubscriptionCardProps[]): OfferPerGroup[] =>
    cards.map(subscriptionCard => ({
      groupName: subscriptionCard.cardName ?? '',
      offerCode: subscriptionCard.selectedOffer ?? '',
      addonCode: subscriptionCard.selectedAddon ?? '',
    }));
  const [selectedOffersPerGroup, setSelectedOffersPerGroup] = useState<OfferPerGroup[]>(() =>
    mapToDefaultSelection(allStaticContent)
  );
  const [selectedOfferGuidPerCard, setSelectedOfferGuidPerCard] = useState<Record<string, string>>(
    allStaticContent
      .map(card => ({ name: card.cardName || '', guid: card.selectedOffer || '' }))
      .reduce((acc, cur) => ({ ...acc, [cur.name]: cur.guid }), {})
  );

  const modelCodesToFetch = expandRelatedModelCodes([onlineModelType]);

  useEffect(() => {
    // Compute inside effect to avoid unstable array reference in deps
    const codes = expandRelatedModelCodes([onlineModelType]);
    fetchModelsByCodes(codes);
  }, [fetchModelsByCodes, onlineModelType, authenticatedUser?.activeAccountMasterId]);

  const voucherModels = activeVoucherCode ? priceAlteredModels.onlineModelsByVoucher?.[activeVoucherCode] : undefined;
  const voucherModelsForType = filterByOnlineModelCodes(voucherModels, modelCodesToFetch);
  const fetchedModelsRaw =
    voucherModelsForType.length > 0
      ? voucherModelsForType
      : filterByOnlineModelCodes(priceAlteredModels.onlineModelsByCode, modelCodesToFetch);
  const fetchedModels = mergeMobileBroadbandModels(fetchedModelsRaw);

  // Gate voucher data on whether the voucher slot has models relevant to this component's model type.
  const hasVoucherModels = voucherModelsForType.length > 0;

  // Create stable dependency for useEffect: stringify model codes to avoid infinite loops
  const fetchedModelCodes = fetchedModels
    .map(m => m.onlineModelCode)
    .sort((a, b) => a.localeCompare(b))
    .join(',');

  useEffect(() => {
    if (fetchedModels.length === 0) {
      setNormalizedModel(undefined);
      return;
    }

    let isSubscribed = true;
    setOfferAndFeeMap([]);

    fetchAddOnRules().then(addOnRules => {
      if (!isSubscribed) {
        return;
      }

      const associations = (addOnRules.associations?.[0] as AssociationRecord[]) ?? [];
      // LiikkuvaWifi is a virtual type — WiFi offers live in the MobileBroadband model
      const modelTypeForLookup =
        onlineModelType === VirtualModelType.LiikkuvaWifi ? ModelType.MobileBroadband : (onlineModelType as ModelType);
      const normalizedOnlineModel = getOnlineModel(modelTypeForLookup, associations, false, fetchedModels);
      const feeMap = prepareOfferAndServiceFeeMap(false, addOnRules, fetchedModelsRaw);

      setNormalizedModel(normalizedOnlineModel ?? fetchedModels[0]);
      setOfferAndFeeMap(feeMap);
      setCloudSenseAssociations(associations);
    });

    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedModelCodes, onlineModelType, authenticatedUser?.activeAccountMasterId, activeVoucherCode]);

  // Fetch campaign contexts + promotions when a voucher is active (for badge matching)
  const [campaignContextsFromVoucher, setCampaignContextsFromVoucher] = useState<CampaignContext[]>([]);
  const [campaignPromotionsFromVoucher, setCampaignPromotionsFromVoucher] = useState<CampaignPromotion[]>([]);
  useEffect(() => {
    if (!activeVoucherCode) {
      setCampaignContextsFromVoucher([]);
      setCampaignPromotionsFromVoucher([]);
      return;
    }
    fetchVoucherCampaigns(activeVoucherCode, expandRelatedModelCodes([onlineModelType])).then(
      ({ campaignContexts: contexts, campaignPromotions: promotions }) => {
        setCampaignContextsFromVoucher(contexts);
        setCampaignPromotionsFromVoucher(promotions);
      },
      () => {
        setCampaignContextsFromVoucher([]);
        setCampaignPromotionsFromVoucher([]);
      }
    );
  }, [activeVoucherCode, onlineModelType]);

  const campaignPromotions = hasVoucherModels
    ? [...(normalizedModel?.campaigns ?? []), ...campaignPromotionsFromVoucher]
    : [...(normalizedModel?.campaigns ?? [])];

  const derivedSubscriptionCards: SubCardWithAnalyticsV4Props[] = normalizedModel
    ? getSubscriptionCardContent(
        false,
        {
          voucher: hasVoucherModels ? activeVoucherCode || undefined : undefined,
          campaignContexts: hasVoucherModels ? campaignContextsFromVoucher : [],
        },
        campaignPromotions,
        allStaticContent,
        selectedOffersPerGroup,
        setSelectedOffersPerGroup,
        undefined,
        normalizedModel
      )
    : [];

  const cardsWithServiceFee = getCardContentWithServiceFee(derivedSubscriptionCards, offerAndFeeMap, false);

  const cardsToRender = cardsWithServiceFee
    .map(card => {
      const selectedGroup = selectedOffersPerGroup.find(g => g.groupName === card.cardName);

      // LiikkuvaWifi cards keep their static sales-product metadata for basket ordering.
      const staticSalesProductPage = (card as MobileBroadbandSubCardProps).publicPage;
      if (onlineModelType === VirtualModelType.LiikkuvaWifi && staticSalesProductPage) {
        return {
          ...card,
          publicPage: {
            ...staticSalesProductPage,
            addonCode: selectedGroup?.addonCode,
          },
        };
      }

      // Normalized MBB cards need their source model code restored for basket routing.
      const sourceModelCode = card.offer?.offerCode
        ? fetchedModelsRaw.find(m => m.offers?.some(o => o.offerCode === card.offer?.offerCode))?.onlineModelCode
        : card.onlineModel?.onlineModelCode;

      return {
        ...card,
        publicPage: {
          offerCode: card.offer?.offerCode,
          onlineModelCode: sourceModelCode ?? card.onlineModel?.onlineModelCode,
          addonCode: selectedGroup?.addonCode,
        },
      };
    })
    .filter(
      card =>
        !normalizedHeaderFilters?.length ||
        normalizedHeaderFilters.some(filter => card.mainHeader?.toLowerCase().includes(filter))
    ) as SubscriptionCardWithModel[];

  // Shared handler for card clicks - opens modal at wrapper level
  const handleCardClick = (card: SubscriptionCardWithModel, cardModalDescriptionItems?: ModalDescriptionItem[]) => {
    if ('offer' in card) {
      setSelectedCard(card as SubCardWithAnalyticsV4Props);
      setSelectedCompactCardName(card.cardName);
      setSelectedModalDescriptionItems(cardModalDescriptionItems ?? []);
      setIsModalOpen(true);
    }
  };

  // MBB-specific handlers (defined at component level, not inside conditional)
  const getCard = (cardName: string) => cardsToRender?.find(card => card.cardName === cardName);

  const handleMBBOrder = (orderProps: PublicPageOrderProps | OrderProps) => {
    const isPublicPageOrder = (props: PublicPageOrderProps | OrderProps): props is PublicPageOrderProps =>
      'offerCode' in props;

    if (!isPublicPageOrder(orderProps)) {
      addOfferToBasket(orderProps as PublicPageOrderProps);
      return;
    }

    const card = cardsToRender.find(c => {
      const cardWithOffer = c as SubCardWithAnalyticsV4Props;
      return cardWithOffer.offer?.offerCode === orderProps.offerCode;
    });

    if (card && 'offer' in card) {
      setSelectedCard(card as SubCardWithAnalyticsV4Props);
      setIsModalOpen(true);
    } else {
      addOfferToBasket(orderProps);
    }
  };

  const handleModalOrder = (orderProps: PublicPageOrderProps | OrderProps, addons?: ChildOrderItem[]) => {
    setIsModalOpen(false);

    if (!selectedCard || !('offer' in selectedCard)) {
      return;
    }

    const cardWithOffer = selectedCard as SubCardWithAnalyticsV4Props;
    const offerToOrder =
      cardWithOffer.onlineModel.offers.find(offer => offer.offerCode === orderProps.offerGuid) ?? cardWithOffer.offer;
    const modalCommercialProduct = offerToOrder.commercialProducts[0];

    // Convert ChildOrderItem[] to addonCodes string array for addSubscriptionToShoppingBasket
    const addonCodes = addons?.map(addon => addon.addOnCode) ?? [];

    // Extract addon attributes (e.g. voicemail language) from ChildOrderItems
    const addonAttributes = addons?.reduce<{ [addOnCode: string]: { [key: string]: string } }>((acc, a) => {
      if (a.addOnAttributes && Object.keys(a.addOnAttributes).length > 0) {
        acc[a.addOnCode] = a.addOnAttributes;
      } else if (isMobileVoiceAddOn(a.addOnCode)) {
        acc[a.addOnCode] = { language: getDefaultSelectedLanguage() };
      }
      return acc;
    }, {});

    addSubscriptionToShoppingBasket(
      cardWithOffer.onlineModel,
      offerToOrder,
      modalCommercialProduct,
      Boolean(authenticatedUser?.activeAccountMasterId),
      orderProps.quantity ?? 1,
      addonCodes,
      cloudSenseAssociations,
      activeVoucherCode || undefined,
      addonAttributes && Object.keys(addonAttributes).length > 0 ? addonAttributes : undefined
    );
  };

  const getModalCard = () => {
    if (compact && onlineModelType === ModelType.YrityspuheUusi) {
      return cardsToRender.find(card => card.cardName === selectedCompactCardName) as
        | SubCardWithAnalyticsV4Props
        | undefined;
    }
    if (selectedCard && 'offer' in selectedCard) {
      return selectedCard as SubCardWithAnalyticsV4Props;
    }
    return undefined;
  };
  const modalCard = getModalCard();
  const commercialProduct = modalCard?.offer?.commercialProducts[0];
  const getCompactModalDescriptionItems = () => {
    if (!compact || !modalCard) {
      return selectedModalDescriptionItems;
    }
    if (modalCard.cardName === 'puheplus') {
      return getPuhePlusModalDescriptionItems(selectedOfferGuidPerCard, modalActivePopoverRef);
    }
    if (modalCard.cardName === 'puhepro') {
      return getPuheProModalDescriptionItems(selectedOfferGuidPerCard, modalActivePopoverRef);
    }
    return selectedModalDescriptionItems;
  };
  const modalDescriptionItems = getCompactModalDescriptionItems();

  if (!normalizedModel) {
    return <Loading big={true} />;
  }

  if (headerFilter?.length && normalizedModel && cardsToRender.length === 0) {
    console.warn('No subscription cards match the provided header filter:', headerFilter);
    return null;
  }

  const analyticsListName = `${eventOrigin} ${getAnalyticsListName(onlineModelType as ModelType)}`;

  if (onlineModelType === ModelType.MobileM2M) {
    return (
      <HorizontalM2MSubscriptions
        subscriptionCards={cardsToRender}
        analyticsListName={analyticsListName}
        isChangeOffer={false}
        offerAndFeeMap={offerAndFeeMap}
        hideGroupHeaders={Boolean(headerFilter?.length)}
        compact={compact}
      />
    );
  }

  if (onlineModelType === ModelType.YrityspuheUusi) {
    return (
      <>
        <HorizontalVoiceSubscriptions
          subscriptionCards={cardsToRender}
          analyticsListName={analyticsListName}
          isChangeOffer={false}
          offerAndFeeMap={offerAndFeeMap}
          hideNumberSelection={true}
          showAddonConfiguration={true}
          onCardClick={handleCardClick}
          hideGroupHeaders={Boolean(headerFilter?.length)}
          wide={wide}
          compact={compact}
          selectedOfferPerGroup={selectedOfferGuidPerCard}
          onSelectOffer={(guid: string, groupName: string) => {
            setSelectedOfferGuidPerCard(prev => ({ ...prev, [groupName]: guid }));
            setSelectedOffersPerGroup(prev => [
              ...prev.filter(off => off.groupName !== groupName),
              { groupName: groupName || '', offerCode: guid },
            ]);
          }}
        />
        {isModalOpen && modalCard && commercialProduct && (
          <SubscriptionModal
            header={
              modalCard.modalHeader ??
              modalCard.mainHeader ??
              modalCard.offer.offerName ??
              t.NUCX('Subscription contents')
            }
            descriptionItems={
              modalDescriptionItems.length > 0 ? modalDescriptionItems : getModalDescriptionItems(modalCard)
            }
            onCloseDialog={() => setIsModalOpen(false)}
            onOrder={handleModalOrder}
            onlineModel={modalCard.onlineModel}
            subscriptionCard={modalCard}
            offers={modalCard.offers}
            hasQuantity={true}
            pricePreamble={modalCard.pricePreamble}
            showAddonConfiguration={true}
            showAlternateProductChoice={Boolean(compact)}
            onSelectOffer={(guid: string, groupName: string) => {
              setSelectedOfferGuidPerCard(prev => ({ ...prev, [groupName]: guid }));
              setSelectedOffersPerGroup(prev => [
                ...prev.filter(off => off.groupName !== groupName),
                { groupName: groupName || '', offerCode: guid },
              ]);
              setSelectedCard(previousCard =>
                previousCard && 'offer' in previousCard ? { ...previousCard, selectedOffer: guid } : previousCard
              );
            }}
            commercialProduct={commercialProduct}
            stickyFooter={true}
            showNewOrKeepNumber={false}
          />
        )}
      </>
    );
  }

  if (onlineModelType === VirtualModelType.LiikkuvaWifi) {
    const wifiModelCodes = Object.values(LiikkuvaWifiSalesProductOnlineModel) as string[];
    const salesProductOnlineModels = fetchedModelsRaw.filter(m => wifiModelCodes.includes(m.onlineModelCode));
    if (salesProductOnlineModels.length === 0) {
      return <Loading big={true} />;
    }
    return (
      <HorizontalMobileBroadbandPublicSubscriptions
        subscriptionCards={cardsToRender}
        salesProductOnlineModels={salesProductOnlineModels}
        renderComponent={HorizontalMBBLiikkuvaWifiSubscriptions}
        eventOrigin={eventOrigin}
        wide={wide}
        compact={compact}
        hideGroupHeaders={Boolean(headerFilter?.length)}
        showDescriptions={!headerFilter?.length}
      />
    );
  }

  if (onlineModelType === ModelType.MobileBroadband) {
    return (
      <>
        <HorizontalMBBYritysdataSubscriptions
          subscriptionCards={cardsToRender}
          getCard={getCard}
          onOrder={handleMBBOrder}
          isChangeOffer={false}
          buttonText={t.SDWF(exploreAndOrderMsg)}
          showDescriptions={!headerFilter?.length}
          hasQuantity={false}
          hideGroupHeaders={Boolean(headerFilter?.length)}
          wide={wide}
          compact={compact}
          eventOrigin={eventOrigin}
        />
        {isModalOpen && modalCard && commercialProduct && (
          <SubscriptionModal
            header={modalCard.mainHeader ?? modalCard.offer.offerName ?? t.NUCX('Subscription contents')}
            descriptionItems={getModalDescriptionItems(modalCard)}
            onCloseDialog={() => setIsModalOpen(false)}
            onOrder={handleModalOrder}
            onlineModel={modalCard.onlineModel}
            subscriptionCard={modalCard}
            hasQuantity={true}
            pricePreamble={modalCard.pricePreamble}
            showAddonConfiguration={true}
            commercialProduct={commercialProduct}
            stickyFooter={true}
          />
        )}
      </>
    );
  }

  console.warn(`SubscriptionCardsByModelTypeWrapper does not support model type: ${onlineModelType}`);
  return null;
};
